import React, { useEffect, useRef, useState, useMemo } from 'react';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';

// Fix for default marker icons in Leaflet
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: require('leaflet/dist/images/marker-icon-2x.png'),
  iconUrl: require('leaflet/dist/images/marker-icon.png'),
  shadowUrl: require('leaflet/dist/images/marker-shadow.png'),
});

// ---------- helpers ----------------------------------------------------------

// Haversine distance in nautical miles between two [lat, lng] points
const distanceNm = (a, b) => {
  const R = 3440.065; // earth radius in nautical miles
  const toRad = (d) => (d * Math.PI) / 180;
  const dLat = toRad(b[0] - a[0]);
  const dLng = toRad(b[1] - a[1]);
  const lat1 = toRad(a[0]);
  const lat2 = toRad(b[0]);
  const h =
    Math.sin(dLat / 2) ** 2 +
    Math.cos(lat1) * Math.cos(lat2) * Math.sin(dLng / 2) ** 2;
  return 2 * R * Math.asin(Math.sqrt(h));
};

// Distance in meters (for stationary detection)
const distanceM = (a, b) => distanceNm(a, b) * 1852;

// Bearing (heading) in degrees from a -> b
const bearing = (a, b) => {
  const toRad = (d) => (d * Math.PI) / 180;
  const toDeg = (r) => (r * 180) / Math.PI;
  const lat1 = toRad(a[0]);
  const lat2 = toRad(b[0]);
  const dLng = toRad(b[1] - a[1]);
  const y = Math.sin(dLng) * Math.cos(lat2);
  const x =
    Math.cos(lat1) * Math.sin(lat2) -
    Math.sin(lat1) * Math.cos(lat2) * Math.cos(dLng);
  return (toDeg(Math.atan2(y, x)) + 360) % 360;
};

const parseTime = (item) => {
  const raw = item.timestamp ?? item.time ?? item.datetime ?? item.date ?? null;
  if (raw == null) return null;
  const t = typeof raw === 'number' ? raw : Date.parse(raw);
  return Number.isNaN(t) ? null : t;
};

const fmtTime = (ms) =>
  ms == null
    ? ''
    : new Date(ms).toLocaleString([], {
        hour: '2-digit',
        minute: '2-digit',
        month: 'short',
        day: 'numeric',
      });

const fmtTimeNoDate = (ms) =>
  ms == null
    ? ''
    : new Date(ms).toLocaleString([], {
        hour: '2-digit',
        minute: '2-digit',
      });

const fmtDuration = (ms) => {
  if (!ms || ms < 0) return '0m';
  const totalMin = Math.round(ms / 60000);
  const h = Math.floor(totalMin / 60);
  const m = totalMin % 60;
  return h > 0 ? `${h}h ${m}m` : `${m}m`;
};

// Boat marker icon (rotatable via CSS transform)
const makeBoatIcon = (heading = 0) =>
  L.divIcon({
    className: 'boat-marker',
    html: `<div style="
        transform: rotate(${heading}deg);
        width: 0; height: 0;
        border-left: 8px solid transparent;
        border-right: 8px solid transparent;
        border-bottom: 20px solid #ff3b30;
        filter: drop-shadow(0 0 2px rgba(0,0,0,0.5));
      "></div>`,
    iconSize: [16, 20],
    iconAnchor: [8, 10],
  });

// Map a 0..1 fraction to the blue -> yellow -> red speed gradient.
// Mirrors the legend and the old hotline palette, but pure JS (no plugin).
const speedColor = (frac) => {
  const f = Math.max(0, Math.min(1, frac));
  const lerp = (a, b, t) => Math.round(a + (b - a) * t);
  // stops: 0.0 #2c7bb6 (blue), 0.5 #ffff8c (yellow), 1.0 #d7191c (red)
  let r, g, b;
  if (f < 0.5) {
    const t = f / 0.5;
    r = lerp(0x2c, 0xff, t);
    g = lerp(0x7b, 0xff, t);
    b = lerp(0xb6, 0x8c, t);
  } else {
    const t = (f - 0.5) / 0.5;
    r = lerp(0xff, 0xd7, t);
    g = lerp(0xff, 0x19, t);
    b = lerp(0x8c, 0x1c, t);
  }
  return `rgb(${r}, ${g}, ${b})`;
};

// A point is "stationary" if it stays within this many meters
const STATIONARY_RADIUS_M = 15;
// ...and remains so for at least this long, to be worth a "moored" marker
const STATIONARY_MIN_MS = 15 * 60 * 1000; // 15 minutes

// True when a Peplink locationMap has at least one usable lat/lng point.
// Layouts use this to decide between the Peplink track and the Starlink
// live fallback, so both agree on what "has GPS" means.
export const hasLocationTrack = (locationMap) =>
  Array.isArray(locationMap) &&
  locationMap.some((p) => p && p.latitude != null && p.longitude != null);

// ---------- component --------------------------------------------------------

/**
 * Props:
 *   locationMap   Peplink GPS history [{latitude, longitude, timestamp, ...}].
 *                 Drawn as a speed-coloured track with playback.
 *   liveLocation  Starlink/ELCOME fallback, used ONLY when locationMap has no
 *                 usable points: {lat, lng, timestamp, approximate,
 *                 accuracy_km, kit_serial, stale}. It's a single live point
 *                 (not history), so no track, no summary and no playback -
 *                 just a marker plus an approximate-area circle.
 *   isHistoricalView  true when the page shows a past date / weekly / monthly.
 *                 Only changes the wording of the live notice.
 */
const LeafletMap = ({ locationMap, liveLocation, isHistoricalView = false }) => {
  const mapRef = useRef(null);
  const mapInstanceRef = useRef(null);
  const segmentsRef = useRef([]);
  const pointMarkersRef = useRef([]);
  const stationaryMarkersRef = useRef([]);
  const boatMarkerRef = useRef(null);
  const startEndRef = useRef([]);
  const animRef = useRef(null);

  const [playing, setPlaying] = useState(false);
  const [frame, setFrame] = useState(0);

  // Live (Starlink) mode: no Peplink history, but a single live position.
  const isLive =
    !hasLocationTrack(locationMap) &&
    liveLocation?.lat != null &&
    liveLocation?.lng != null;

  // Build enriched track: [{lat,lng,speed,heading,time}]
  const track = useMemo(() => {
    if (isLive) {
      const t = liveLocation.timestamp ? Date.parse(liveLocation.timestamp) : null;
      return [{
        lat: liveLocation.lat,
        lng: liveLocation.lng,
        speed: 0,
        heading: 0,
        time: Number.isNaN(t) ? null : t,
      }];
    }
    if (!locationMap || locationMap.length === 0) return [];

    const seen = new Set();
    const pts = [];
    locationMap.forEach((item) => {
      const lat = item.latitude;
      const lng = item.longitude;
      if (lat == null || lng == null) return;
      const t = parseTime(item);
      const key = t != null ? `t_${t}` : `${lat.toFixed(6)}_${lng.toFixed(6)}`;
      if (seen.has(key)) return;
      seen.add(key);
      pts.push({
        lat,
        lng,
        rawSpeed: item.speed ?? item.sog ?? null, // knots if provided
        rawHeading: item.heading ?? item.he ?? null, // degrees if provided
        time: t,
      });
    });

    // Keep chronological order if we have timestamps
    if (pts.every((p) => p.time != null)) {
      pts.sort((a, b) => a.time - b.time);
    }

    for (let i = 0; i < pts.length; i++) {
      const prev = pts[i - 1];
      const cur = pts[i];
      if (cur.rawHeading != null) {
        cur.heading = cur.rawHeading;
      } else if (prev) {
        cur.heading = bearing([prev.lat, prev.lng], [cur.lat, cur.lng]);
      } else {
        cur.heading = 0;
      }
      if (cur.rawSpeed != null) {
        cur.speed = cur.rawSpeed;
      } else if (prev && prev.time != null && cur.time != null && cur.time > prev.time) {
        const d = distanceNm([prev.lat, prev.lng], [cur.lat, cur.lng]);
        const hours = (cur.time - prev.time) / 3600000;
        cur.speed = hours > 0 ? d / hours : 0;
      } else {
        cur.speed = 0;
      }
    }
    return pts;
  }, [locationMap, isLive, liveLocation]);

  const hasTime = useMemo(() => track.some((p) => p.time != null), [track]);
  const maxSpeed = useMemo(
    () => Math.max(1, ...track.map((p) => p.speed || 0)),
    [track]
  );

  // Detect stationary runs (consecutive points clustered in one spot)
  const stationaryRuns = useMemo(() => {
    if (isLive || !hasTime || track.length < 2) return [];
    const runs = [];
    let i = 0;
    while (i < track.length) {
      let j = i + 1;
      while (
        j < track.length &&
        distanceM([track[i].lat, track[i].lng], [track[j].lat, track[j].lng]) <=
          STATIONARY_RADIUS_M
      ) {
        j++;
      }
      const startP = track[i];
      const endP = track[j - 1];
      const dur = (endP.time ?? 0) - (startP.time ?? 0);
      if (j - i >= 2 && dur >= STATIONARY_MIN_MS) {
        runs.push({ startIdx: i, endIdx: j - 1, start: startP, end: endP, dur });
      }
      i = j > i + 1 ? j : i + 1;
    }
    return runs;
  }, [track, hasTime, isLive]);

  // Trip summary
  const summary = useMemo(() => {
    if (isLive || track.length === 0) return null;
    let dist = 0;
    for (let i = 1; i < track.length; i++) {
      dist += distanceNm(
        [track[i - 1].lat, track[i - 1].lng],
        [track[i].lat, track[i].lng]
      );
    }
    const totalMs =
      hasTime && track.length > 1
        ? track[track.length - 1].time - track[0].time
        : 0;
    const mooredMs = stationaryRuns.reduce((s, r) => s + r.dur, 0);
    const underwayMs = Math.max(0, totalMs - mooredMs);
    const speeds = track.map((p) => p.speed || 0);
    const avg = speeds.reduce((a, b) => a + b, 0) / (speeds.length || 1);
    return {
      distanceNm: dist,
      totalMs,
      mooredMs,
      underwayMs,
      avgSpeed: avg,
      maxSpeed,
    };
  }, [track, hasTime, stationaryRuns, maxSpeed, isLive]);

  // ----- init map once -----
  useEffect(() => {
    if (mapInstanceRef.current || track.length === 0) return;

    const map = L.map(mapRef.current, {
      center: [track[0].lat, track[0].lng],
      zoom: 13,
      zoomControl: true,
    });
    mapInstanceRef.current = map;

    const osmBase = L.tileLayer(
      'https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png',
      { attribution: '&copy; OpenStreetMap contributors', maxZoom: 19, className: 'll-dark-tiles' }
    );
    const seamarks = L.tileLayer(
      'https://tiles.openseamap.org/seamark/{z}/{x}/{y}.png',
      { attribution: '&copy; OpenSeaMap contributors', maxZoom: 18, opacity: 0.9 }
    );

    osmBase.addTo(map);
    seamarks.addTo(map);

    L.control
      .layers(
        { 'Street (OSM)': osmBase },
        { 'Sea marks (OpenSeaMap)': seamarks },
        { collapsed: true }
      )
      .addTo(map);
    L.control.scale({ metric: true, imperial: true }).addTo(map);

    return () => {
      map.remove();
      mapInstanceRef.current = null;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [track.length === 0]);

  // ----- draw / redraw track -----
  useEffect(() => {
    const map = mapInstanceRef.current;
    if (!map || track.length === 0) return;

    segmentsRef.current.forEach((s) => s.remove());
    segmentsRef.current = [];
    pointMarkersRef.current.forEach((m) => m.remove());
    pointMarkersRef.current = [];
    stationaryMarkersRef.current.forEach((m) => m.remove());
    stationaryMarkersRef.current = [];
    startEndRef.current.forEach((m) => m.remove());
    startEndRef.current = [];
    if (boatMarkerRef.current) boatMarkerRef.current.remove();
    boatMarkerRef.current = null;

    // ----- live (Starlink) mode: one approximate point, nothing to animate -----
    if (isLive) {
      const p = track[0];
      const radiusM = (liveLocation.accuracy_km || 10) * 1000;
      const area = L.circle([p.lat, p.lng], {
        radius: radiusM,
        color: '#88F4EB',
        weight: 1.5,
        dashArray: '6 6',
        fillColor: '#88F4EB',
        fillOpacity: 0.08,
        interactive: false,
      }).addTo(map);
      const pin = L.marker([p.lat, p.lng], {
        icon: L.divIcon({
          className: '',
          html: `<div class="ll-live-pin"><span></span></div>`,
          iconSize: [18, 18],
          iconAnchor: [9, 9],
        }),
        zIndexOffset: 1000,
      })
        .bindPopup(
          `<strong>Starlink position (live)</strong><br/>` +
            (p.time != null ? `As of: ${fmtTime(p.time)}<br/>` : '') +
            (liveLocation.kit_serial ? `Kit: ${liveLocation.kit_serial}<br/>` : '') +
            `Approx. &plusmn;${liveLocation.accuracy_km || 10} km`
        )
        .addTo(map);
      startEndRef.current = [area, pin];
      map.fitBounds(area.getBounds(), { padding: [30, 30], maxZoom: 11 });
      setFrame(0);
      setPlaying(false);
      return;
    }

    // Speed-colored track, drawn as one short polyline per segment. Pure core
    // Leaflet (no leaflet-hotline plugin), which avoids the canvas-context
    // "clearRect / save of undefined" errors from the plugin on Leaflet 1.9.
    for (let i = 1; i < track.length; i++) {
      const a = track[i - 1];
      const b = track[i];
      // color by the average speed of the two endpoints
      const frac = ((a.speed || 0) + (b.speed || 0)) / 2 / maxSpeed;
      const seg = L.polyline(
        [
          [a.lat, a.lng],
          [b.lat, b.lng],
        ],
        {
          color: speedColor(frac),
          weight: 5,
          opacity: 0.9,
          lineCap: 'round',
          lineJoin: 'round',
        }
      ).addTo(map);
      segmentsRef.current.push(seg);
    }

    // Which point indices are inside a collapsed stationary run?
    const collapsed = new Set();
    stationaryRuns.forEach((r) => {
      for (let k = r.startIdx; k <= r.endIdx; k++) collapsed.add(k);
    });

    // Small clickable points (skip ones hidden inside a moored cluster)
    track.forEach((p, i) => {
      if (collapsed.has(i)) return;
      const marker = L.circleMarker([p.lat, p.lng], {
        radius: 3,
        fillColor: '#333',
        color: '#fff',
        weight: 1,
        opacity: 0.9,
        fillOpacity: 0.7,
      }).addTo(map);
      marker.bindPopup(
        `<strong>Point ${i + 1}</strong><br/>` +
          (p.time != null ? `Time: ${fmtTime(p.time)}<br/>` : '') +
          `Speed: ${(p.speed || 0).toFixed(1)} kn<br/>` +
          `Heading: ${Math.round(p.heading || 0)}&deg;`
      );
      pointMarkersRef.current.push(marker);
    });

    // Moored markers for stationary runs
    stationaryRuns.forEach((r) => {
      const anchorIcon = L.divIcon({
        className: '',
        html: `<div style="
            width:22px;height:22px;border-radius:50%;
            background:#f39c12;border:2px solid #fff;color:#fff;
            display:flex;align-items:center;justify-content:center;
            font-size:12px;font-weight:700;
            box-shadow:0 0 4px rgba(0,0,0,.5)">&#9875;</div>`,
        iconSize: [22, 22],
        iconAnchor: [11, 11],
      });
      const m = L.marker([r.start.lat, r.start.lng], { icon: anchorIcon })
        .bindPopup(
          `<strong>Moored / stopped</strong><br/>` +
            `From: ${fmtTime(r.start.time)}<br/>` +
            `To: ${fmtTime(r.end.time)}<br/>` +
            `Duration: ${fmtDuration(r.dur)}`
        )
        .addTo(map);
      stationaryMarkersRef.current.push(m);
    });

    // start & end markers
    const mkDot = (color) =>
      L.divIcon({
        className: '',
        html: `<div style="width:14px;height:14px;border-radius:50%;background:${color};border:2px solid #fff;box-shadow:0 0 3px rgba(0,0,0,.5)"></div>`,
        iconSize: [14, 14],
        iconAnchor: [7, 7],
      });
    const s = L.marker([track[0].lat, track[0].lng], { icon: mkDot('#2ecc71') })
      .bindPopup('Start')
      .addTo(map);
    const e = L.marker(
      [track[track.length - 1].lat, track[track.length - 1].lng],
      { icon: mkDot('#e74c3c') }
    )
      .bindPopup('End')
      .addTo(map);
    startEndRef.current = [s, e];

    // animated boat marker
    boatMarkerRef.current = L.marker([track[0].lat, track[0].lng], {
      icon: makeBoatIcon(track[0].heading || 0),
      zIndexOffset: 1000,
    }).addTo(map);

    const bounds = L.latLngBounds(track.map((p) => [p.lat, p.lng]));
    map.fitBounds(bounds, { padding: [50, 50], maxZoom: 15 });

    setFrame(0);
    setPlaying(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [track, maxSpeed, stationaryRuns, isLive]);

  // ----- move boat when frame changes -----
  useEffect(() => {
    const boat = boatMarkerRef.current;
    if (!boat || track.length === 0) return;
    const p = track[Math.min(frame, track.length - 1)];
    boat.setLatLng([p.lat, p.lng]);
    boat.setIcon(makeBoatIcon(p.heading || 0));
  }, [frame, track]);

  // ----- playback loop -----
  useEffect(() => {
    if (!playing) {
      if (animRef.current) clearInterval(animRef.current);
      return;
    }
    animRef.current = setInterval(() => {
      setFrame((f) => {
        if (f >= track.length - 1) {
          setPlaying(false);
          return f;
        }
        return f + 1;
      });
    }, 300);
    return () => clearInterval(animRef.current);
  }, [playing, track.length]);

  if (!track || track.length === 0) return null;

  const current = track[Math.min(frame, track.length - 1)];

  return (
    <div className="ll-wrap">
        {/* live (Starlink) notice instead of the trip summary */}
        {isLive && (
          <div className="ll-live-note">
            <span className="ll-live-badge">
              <span className="ll-live-dot" /> LIVE · STARLINK
            </span>
            <span>
              No Peplink GPS history{isHistoricalView ? ' for this period' : ''}.
              Showing the current Starlink position
              {current.time != null ? <> as of <strong>{fmtTime(current.time)}</strong></> : null}
              {liveLocation.stale ? ' (last known, not updated today)' : ''}.
              Approximate (&plusmn;{liveLocation.accuracy_km || 10} km) and a single
              point, so track playback is not available.
              {isHistoricalView ? ' This is where the vessel is now, not where it was on the selected dates.' : ''}
            </span>
          </div>
        )}

        {/* trip summary */}
        {summary && (
          <div className="ll-summary">
            <span><strong>Distance:</strong> {summary.distanceNm.toFixed(2)} nm</span>
            {hasTime && (
              <>
                <span><strong>Underway:</strong> {fmtDuration(summary.underwayMs)}</span>
                <span><strong>Moored:</strong> {fmtDuration(summary.mooredMs)}</span>
              </>
            )}
            <span><strong>Avg:</strong> {summary.avgSpeed.toFixed(1)} kn</span>
            <span><strong>Max:</strong> {summary.maxSpeed.toFixed(1)} kn</span>
          </div>
        )}

        <div ref={mapRef} className="ll-map" />

        {/* playback controls (history only) */}
        {!isLive && (
        <div className="ll-controls">
          <button
            type="button"
            className="ll-play-btn"
            onClick={() => {
              if (frame >= track.length - 1) setFrame(0);
              setPlaying((p) => !p);
            }}
          >
            {playing ? 'Pause' : 'Play'}
          </button>
          <input
            type="range"
            min={0}
            max={track.length - 1}
            value={frame}
            onChange={(e) => {
              setPlaying(false);
              setFrame(Number(e.target.value));
            }}
            className="ll-scrubber"
          />
          <span className="ll-controls-time">
            {hasTime ? fmtTimeNoDate(current.time) : `Point ${frame + 1}/${track.length}`}
            {' · '}
            {(current.speed || 0).toFixed(1)} kn
          </span>
        </div>
        )}

        {/* speed legend (history only) */}
        {!isLive && (
        <div className="ll-legend">
          <span>Slow</span>
          <div className="ll-legend-gradient" />
          <span>Fast ({maxSpeed.toFixed(1)} kn)</span>
        </div>
        )}

        <style dangerouslySetInnerHTML={{ __html: `
          .ll-wrap { background: var(--navy-800, var(--color-background)); }
          .ll-summary {
            display: flex; flex-wrap: wrap; gap: 18px; padding: 10px 14px;
            background: var(--navy-700, var(--color-surface)); border-bottom: 1px solid var(--line, rgba(255,255,255,.1));
            font: 400 12px var(--font-mono, monospace); color: var(--text-dim, var(--color-text-secondary));
          }
          .ll-map { height: 400px; width: 100%; min-height: 400px; }
          .ll-map .ll-dark-tiles { filter: invert(1) hue-rotate(180deg) brightness(0.92) contrast(0.9) saturate(0.7); }
          .ll-controls {
            display: flex; align-items: center; gap: 12px; padding: 10px 14px;
            background: var(--navy-700, var(--color-surface)); border-top: 1px solid var(--line, rgba(255,255,255,.1));
            font: 400 12px var(--font-mono, monospace);
          }
          .ll-play-btn {
            border: none; background: var(--neon, #CAFB6D); color: var(--navy-800, #112023);
            padding: 7px 15px; cursor: pointer; font: 700 11px var(--font-sans, sans-serif); letter-spacing: 0.04em;
          }
          .ll-play-btn:hover { opacity: 0.88; }
          .ll-scrubber { flex: 1; accent-color: var(--turquoise, #88F4EB); }
          .ll-controls-time { white-space: nowrap; color: var(--text-dim, var(--color-text-secondary)); }
          .ll-legend {
            display: flex; align-items: center; gap: 8px; padding: 8px 14px;
            background: var(--navy-700, var(--color-surface)); border-top: 1px solid var(--line, rgba(255,255,255,.1));
            font: 400 10.5px var(--font-mono, monospace); color: var(--text-muted, var(--color-label));
          }
          .ll-live-note {
            display: flex; flex-wrap: wrap; align-items: center; gap: 10px 14px; padding: 10px 14px;
            background: var(--navy-700, var(--color-surface)); border-bottom: 1px solid var(--line, rgba(255,255,255,.1));
            font: 400 12px/1.5 var(--font-mono, monospace); color: var(--text-dim, var(--color-text-secondary));
          }
          .ll-live-note strong { color: var(--color-text-primary, #fff); font-weight: 700; }
          .ll-live-badge {
            display: inline-flex; align-items: center; gap: 6px; padding: 4px 9px; white-space: nowrap;
            background: rgba(136, 244, 235, 0.14); color: var(--turquoise, #88F4EB);
            font: 400 10px var(--font-mono, monospace); letter-spacing: 0.1em;
          }
          .ll-live-dot { width: 6px; height: 6px; border-radius: 999px; background: currentColor; animation: llPulse 1.6s ease-in-out infinite; }
          .ll-live-pin {
            position: relative; width: 18px; height: 18px; border-radius: 50%;
            background: rgba(136, 244, 235, 0.25); display: flex; align-items: center; justify-content: center;
          }
          .ll-live-pin span {
            width: 10px; height: 10px; border-radius: 50%; background: #88F4EB;
            border: 2px solid #fff; box-shadow: 0 0 4px rgba(0,0,0,.5);
          }
          .ll-live-pin::after {
            content: ''; position: absolute; inset: 0; border-radius: 50%;
            border: 2px solid #88F4EB; animation: llRing 2s ease-out infinite;
          }
          @keyframes llPulse { 0%,100% { opacity: 1; } 50% { opacity: .35; } }
          @keyframes llRing { 0% { transform: scale(.6); opacity: .9; } 100% { transform: scale(2.2); opacity: 0; } }
          @media (prefers-reduced-motion: reduce) { .ll-live-dot, .ll-live-pin::after { animation: none; } }
          .ll-legend-gradient { flex: 1; height: 6px; background: linear-gradient(to right, #2c7bb6, #ffff8c, #d7191c); }
        ` }} />
    </div>
  );
};

export default LeafletMap;
